import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot } from 'lucide-react';
import { listScrapers } from '@/api/observe';
import ScraperCard from '@/components/ScraperCard';
import BulkUpdatePanel from '@/components/BulkUpdatePanel';
import Empty from '@/components/Empty';
import { cn } from '@/lib/cn';
import type { Executor } from '@/types';

const EXECUTOR_FILTERS: Array<Executor | 'all'> = ['all', 'cf_workers', 'real_browsers', 'docker'];
type StatusFilter = 'active' | 'inactive';
const STATUS_STORAGE_KEY = 'otr.scrapers.statusFilter';

export default function Scrapers() {
  const [exec, setExec] = useState<Executor | 'all'>('all');

  // Status filter (Active / Inactive) is a toggle, not stacked sections
  // — clicking flips which group renders. Persisted to localStorage so
  // an operator who lives in "Inactive" (re-enabling paused scrapers)
  // doesn't get bounced back to "Active" on every page load.
  const [status, setStatus] = useState<StatusFilter>(() => {
    if (typeof window === 'undefined') return 'active';
    const saved = window.localStorage.getItem(STATUS_STORAGE_KEY);
    return saved === 'inactive' ? 'inactive' : 'active';
  });
  useEffect(() => {
    window.localStorage.setItem(STATUS_STORAGE_KEY, status);
  }, [status]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['scrapers'],
    queryFn: listScrapers,
    refetchInterval: 5_000,
  });

  const all = data?.scrapers ?? [];
  const sortByName = (a: typeof all[number], b: typeof all[number]) =>
    a.display_name.localeCompare(b.display_name);

  // Counts for the toggle labels are computed before the executor
  // filter is applied so the totals stay stable as the user flips
  // executor pills — otherwise "Active · 8" could jump to "Active · 2"
  // when filtering to docker, which is confusing UX.
  const activeCount   = all.filter(s => s.enabled).length;
  const inactiveCount = all.filter(s => !s.enabled).length;

  // What renders: filter by status, then by executor, then sort.
  const visible = all
    .filter(s => (status === 'active' ? s.enabled : !s.enabled))
    .filter(s => exec === 'all' || s.executor === exec)
    .sort(sortByName);

  return (
    <div className="space-y-4">
      {/* Fleet-wide bulk editor — sits above the per-scraper grid so an
          operator can change keywords + max-pages across many scrapers
          in one action (e.g. when adding a new product category). */}
      {!isLoading && !isError && all.length > 0 && (
        <BulkUpdatePanel scrapers={all} />
      )}

      {/* Layout shell now owns tab bar + tagline; this page just renders
          the manage-specific controls (executor filter) + scraper grid. */}
      <div className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-md p-0.5 overflow-x-auto max-w-full w-fit">
        {EXECUTOR_FILTERS.map(e => (
          <button
            key={e}
            onClick={() => setExec(e)}
            className={cn(
              'px-2.5 sm:px-3 py-1 text-xs font-medium rounded transition shrink-0 whitespace-nowrap',
              exec === e ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white',
            )}
          >
            {e === 'all' ? 'all' : e.replace('_', ' ')}
          </button>
        ))}
      </div>

      <StatusToggle
        value={status}
        onChange={setStatus}
        activeCount={activeCount}
        inactiveCount={inactiveCount}
      />

      {isLoading && <div className="text-sm text-slate-500">Loading scrapers…</div>}
      {isError && <div className="text-sm text-red-300">Failed to load scrapers.</div>}
      {!isLoading && !isError && all.length === 0 && (
        <Empty
          icon={<Bot className="w-8 h-8" />}
          title="No scrapers registered"
          hint="Scrapers register themselves on first run via the heartbeat helper. Run one and refresh."
        />
      )}
      {!isLoading && !isError && all.length > 0 && visible.length === 0 && (
        <div className="text-sm text-slate-500 px-1">
          No {status} scrapers{exec !== 'all' ? <> in <code className="text-slate-300">{exec.replace('_', ' ')}</code></> : ''}.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {visible.map(s => <ScraperCard key={s.id} scraper={s} />)}
      </div>
    </div>
  );
}

/** Two-way toggle pill — Active vs Inactive — with running counts on
 *  each side. Modeled on the executor-filter pill row above so both
 *  controls feel like one family. Counts reflect the entire registry,
 *  not the executor-filtered subset, so the user can read totals at
 *  a glance without flipping pills.
 */
function StatusToggle({
  value, onChange, activeCount, inactiveCount,
}: {
  value: StatusFilter;
  onChange: (s: StatusFilter) => void;
  activeCount:   number;
  inactiveCount: number;
}) {
  const choices: Array<{ key: StatusFilter; label: string; count: number; dotClass: string }> = [
    { key: 'active',   label: 'Active',   count: activeCount,   dotClass: 'bg-emerald-400' },
    { key: 'inactive', label: 'Inactive', count: inactiveCount, dotClass: 'bg-slate-500'   },
  ];
  return (
    <div
      role="tablist"
      aria-label="Filter scrapers by enabled state"
      className="inline-flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-md p-0.5"
    >
      {choices.map(c => (
        <button
          key={c.key}
          role="tab"
          aria-selected={value === c.key}
          onClick={() => onChange(c.key)}
          className={cn(
            'flex items-center gap-2 px-3 py-1 text-xs font-medium rounded transition whitespace-nowrap',
            value === c.key
              ? 'bg-slate-800 text-white'
              : 'text-slate-400 hover:text-white',
          )}
        >
          <span className={cn('inline-block h-1.5 w-1.5 rounded-full', c.dotClass)} aria-hidden />
          <span>{c.label}</span>
          <span className="text-slate-500 font-normal">·</span>
          <span className="text-slate-400 font-normal">{c.count}</span>
        </button>
      ))}
    </div>
  );
}
